<template>
  <div
    class="d-flex align-center justify-center"
    :class="!mobile ? 'main-container-w-background' : 'main-container'"
  >
    <!-- prevent zoom on input focus -->
    <meta
      v-if="mobile"
      name="viewport"
      content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
    />
    <v-card
      :elevation="mobile ? 0 : 24"
      :class="!mobile ? 'pa-4 password-login-card' : ''"
    >
      <div class="d-flex flex-column align-center justify-center">
        <img
          v-show="hasProperSignInImage"
          v-lazy="getImage(signInInfo.storefront_login_image)"
          class="mb-4 company-logo no_select"
          lazy="loaded"
          data-testid="storefront-signin-logo"
        />
        <!-- No logo uploaded: the full ThreadFlow wordmark, FITTED into the
             tile. The old fallback was a pre-cropped share image drawn with
             object-fit: cover, which showed "eadF" (Bug Intake #395). -->
        <img
          v-show="!hasProperSignInImage"
          :src="defaultSignInLogoSrc"
          alt="ThreadFlow"
          class="mb-4 company-logo company-logo--fit no_select"
          data-testid="storefront-signin-default-logo"
        />
        <div class="d-flex align-center justify-center">
          <v-col cols="12" md="6" sm="10" class="login">
            <v-form
              class="login-form"
              @submit.prevent="submitHandler"
              v-model="valid"
              ref="form"
            >
              <div class="d-flex flex-column align-center justify-center">
                <div style="width: 350px">
                  <v-text-field
                    id="password"
                    prepend-inner-icon="lock"
                    name="password"
                    type="password"
                    v-model="password"
                    :rules="[validateNotEmpty, passwordRules]"
                    placeholder="Password"
                    data-testid="password"
                    variant="solo"
                    rounded
                    @keyup="incorrectPassword = false"
                  >
                  </v-text-field>

                  <!--
                    A 429: this address has used up its wrong-password
                    budget, and even the right password is refused until the
                    window rolls -- so it must not read "Incorrect Password".
                  -->
                  <div
                    v-if="throttled"
                    class="text-error text-body-2 mb-4"
                    role="alert"
                    data-testid="password-wall-throttled"
                  >
                    {{ throttledMessage }}
                  </div>

                  <v-btn
                    block
                    :color="brandColor"
                    type="submit"
                    rounded="pill"
                    :loading="loading"
                    :disabled="!valid || loading"
                    data-testid="submit"
                    :class="['btn-login', `text-${loginButtonTextColor}`]"
                    :key="loginButtonKey"
                    >Start Shopping
                  </v-btn>
                </div>
              </div>
            </v-form>
          </v-col>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import store from "@/store";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import {
  DEFAULT_SIGN_IN_LOGO_SRC,
  SIGN_IN_NO_LOGO_SENTINEL,
} from "@/utils/signInLogo";

export default {
  components: {},
  mixins: [helpers, rules],

  props: {
    campaignID: {
      required: false,
      type: Number,
    },
    brandColor: {
      required: false,
      type: String,
      default: "#000000",
    },
    // Defaulted -- NOT merely optional. This component has two doorways:
    // StorefrontSessionGate renders it inline and DOES pass signInInfo,
    // but `/shop/:slug/signin` (route name `sign-in-test`) mounts it
    // directly as a route component, so nothing passes the prop there.
    //
    // Without a default it arrived `undefined`, and the template's
    // `getImage(signInInfo.storefront_login_image)` dereferenced it during
    // render. `v-show` still evaluates its bound expression, so the
    // null-check in `hasProperSignInImage` below ran too LATE -- the render
    // threw and the whole page came out blank.
    //
    // A default is the single mechanism that fixes it for BOTH doorways at
    // once; scattering `?.` through the template would leave the next
    // unguarded dereference free to reintroduce the same blank page.
    signInInfo: {
      required: false,
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      mobile: window.innerWidth < 960,
      password: null,
      campaign: null,
      loading: false,
      valid: false,
      authenticated: false,
      incorrectPassword: false,
      // The last attempt was refused with a 429 (see the template note).
      throttled: false,
      throttledMessage: "",
      loginButtonKey: 0,
      defaultSignInLogoSrc: DEFAULT_SIGN_IN_LOGO_SRC,
    };
  },

  computed: {
    passwordRules() {
      return !this.incorrectPassword || "Incorrect Password";
    },

    loginButtonTextColor() {
      // if color is too light, set text color to black
      if (this.setBlackIfTooLight(this.brandColor) != this.brandColor) {
        return "black";
      } else {
        return "white";
      }
    },

    hasProperSignInImage() {
      return (
        this.signInInfo &&
        this.signInInfo.storefront_login_image &&
        this.signInInfo.storefront_login_image != SIGN_IN_NO_LOGO_SENTINEL
      );
    },
  },

  watch: {},
  created() {
    window.addEventListener("resize", () => {
      let width = window.innerWidth;
      if (width < 960) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    });
  },

  unmounted() {
    window.removeEventListener("resize", () => {
      let width = window.innerWidth;
      if (width < 960) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    });
  },
  async mounted() {
    this.load();
  },

  methods: {
    load() {},
    // Returns what the server said about the password: "authenticated",
    // "invalid", or null when it gave no verdict (429, network, 5xx).
    async checkPassword() {
      this.authenticated = false;
      this.throttled = false;
      try {
        const result = await this.$axios.post(
          `/campaign/validateStorePassword`,
          {
            campaign_id: this.campaignID,
            password: this.password,
          }
        );
        this.authenticated = result.data === "authenticated";
        return this.authenticated ? "authenticated" : "invalid";
      } catch (err) {
        console.log(describeError(err));
        if (err?.response?.status === 429) {
          const serverMessage = err.response.data?.error;
          this.throttled = true;
          this.throttledMessage =
            typeof serverMessage === "string" && serverMessage.trim()
              ? serverMessage
              : "Too many attempts. Wait a few minutes and try again.";
        }
        this.$store.dispatch("raiseError", err);
        return null;
      }
    },
    async submitHandler() {
      const verdict = await this.checkPassword();
      // Only a real "wrong password" answer says so; a throttled or failed
      // request says nothing about whether the password was right.
      this.incorrectPassword = verdict === "invalid";
      if (verdict === "authenticated") {
        this.$emit("sign-in");
      }
    },
  },
};
</script>

<style scoped>
.main-container-w-background {
  height: 100vh;
  background-color: #f5f8fa;
}

.main-container {
  height: 100%;
  width: 100%;
}

.password-login-card {
  border-radius: 50px;
  width: 400px;
}

.tf-logo {
  image-rendering: crisp-edges;
}
.btn-login {
  height: 48px !important;
}
.company-logo {
  object-fit: cover;
  width: 350px;
  height: 350px;
  border-radius: 30px;
}
/* The default wordmark is wide: show all of it, centered in the tile. */
.company-logo--fit {
  object-fit: contain;
  padding: 24px;
}
</style>

